import { useCallback, useEffect, useRef, useState } from 'react'
import { type RecaptchaVerifier } from 'firebase/auth'
import { auth } from '@/firebase'
import { TwoStepMessages } from '@/constants/messages'
import { reportAuthFailure } from '@/services/authFailureLog'
import {
  finishTextSetup,
  smsVerifier,
  startTextSetup,
} from '@/services/twoFactor'

function errorCode(error: unknown): string | undefined {
  return (error as { code?: string })?.code
}

/** "+1 (647) 555-0100" → "+16475550100" for Firebase. */
function toE164(value: string): string {
  const dial = value.match(/^\+\d+/)?.[0] ?? '+1'
  const local = value.slice(dial.length).replace(/\D/g, '')
  return dial + local
}

function textError(err: unknown): string {
  const c = errorCode(err)
  if (c === 'auth/invalid-phone-number') return TwoStepMessages.invalidPhone
  if (c === 'auth/operation-not-allowed' || c === 'auth/unsupported-region')
    return TwoStepMessages.regionNotAllowed
  if (c === 'auth/invalid-verification-code') return TwoStepMessages.wrongCode
  if (c === 'auth/code-expired') return TwoStepMessages.codeExpired
  if (c === 'auth/too-many-requests') return TwoStepMessages.tooMany
  // No Firebase code: show the error's own text so it can be diagnosed
  return TwoStepMessages.failed(c ?? (err as Error)?.message)
}

/** What went wrong, in the console (user, 2026-11-01: "Could not verify"). */
function logFailure(stage: string, err: unknown, extra: object) {
  const e = err as { name?: string; code?: string; message?: string }
  console.warn(`[TextSetup] ${stage} failed`, {
    name: e?.name,
    code: e?.code,
    message: e?.message,
    ...extra,
  })
}

interface Options {
  /** Id of the empty element the invisible reCAPTCHA binds to. */
  recaptchaId: string
  /** Wraps the send, e.g. to re-check identity on `requires-recent-login`. */
  wrapSend?: (send: () => Promise<void>) => Promise<void>
  /** Runs before each send, e.g. waiting for the confirmed email. */
  beforeSend?: () => Promise<void>
}

/**
 * Adding a text-message second step: phone → code (PB-62). The one copy of
 * this flow, used by the Security tab's "Add text message" dialog and the
 * admin's phone step after accepting an invitation (PB-88, D129).
 */
export function useTextSetup({ recaptchaId, wrapSend, beforeSend }: Options) {
  const [phone, setPhone] = useState('')
  const [country, setCountry] = useState('US')
  const [verificationId, setVerificationId] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const verifier = useRef<RecaptchaVerifier | null>(null)

  /**
   * Drops the reCAPTCHA and its widget. `clear()` alone leaves the widget's
   * element behind, so a new one in the same element fails with "reCAPTCHA has
   * already been rendered in this element" (user, 2026-11-01: Send again /
   * Use a different number). Each verifier gets a fresh child element instead.
   */
  const dropVerifier = useCallback(() => {
    verifier.current?.clear()
    verifier.current = null
    document.getElementById(recaptchaId)?.replaceChildren()
  }, [recaptchaId])

  const freshVerifier = (): RecaptchaVerifier => {
    dropVerifier()
    const host = document.getElementById(recaptchaId)
    if (!host) return smsVerifier(recaptchaId)
    const slot = document.createElement('div')
    host.appendChild(slot)
    return smsVerifier(slot)
  }

  const reset = useCallback(() => {
    setPhone('')
    setVerificationId(null)
    setCode('')
    setError(null)
    dropVerifier()
  }, [dropVerifier])

  useEffect(() => () => verifier.current?.clear(), [])

  /** Back to the phone field, keeping the number typed so far. */
  const changeNumber = () => {
    console.info('[TextSetup] use a different number')
    setVerificationId(null)
    setCode('')
    setError(null)
    dropVerifier()
  }

  /** Sends (or sends again) the code to the entered phone; true when sent. */
  const send = async (): Promise<boolean> => {
    const user = auth.currentUser
    if (!user) return false
    const again = verificationId !== null
    console.info('[TextSetup] send', {
      again,
      hadVerifier: verifier.current !== null,
      container: !!document.getElementById(recaptchaId),
      containerChildren:
        document.getElementById(recaptchaId)?.childElementCount,
    })
    setBusy(true)
    setError(null)
    try {
      const run = async () => {
        await beforeSend?.()
        // A used reCAPTCHA can't be reused: a fresh one for every send
        verifier.current = freshVerifier()
        setVerificationId(
          await startTextSetup(user, toE164(phone), verifier.current)
        )
        setCode('')
      }
      await (wrapSend ? wrapSend(run) : run())
      return true
    } catch (err) {
      logFailure('send', err, { again, recaptchaId })
      reportAuthFailure('text_setup_send', err)
      setError(textError(err))
      return false
    } finally {
      setBusy(false)
    }
  }

  /** Adds the phone as a second step; returns its label, or null on failure. */
  const finish = async (): Promise<string | null> => {
    const user = auth.currentUser
    if (!user || !verificationId) return null
    setBusy(true)
    setError(null)
    try {
      const label = `Text to ${phone}`
      await finishTextSetup(user, verificationId, code, label)
      return label
    } catch (err) {
      logFailure('finish', err, { recaptchaId })
      reportAuthFailure('text_setup_confirm', err)
      setError(textError(err))
      return null
    } finally {
      setBusy(false)
    }
  }

  return {
    phone,
    setPhone,
    country,
    setCountry,
    code,
    setCode: (value: string) => setCode(value.replace(/\D/g, '')),
    codeSent: verificationId !== null,
    error,
    busy,
    send,
    finish,
    reset,
    changeNumber,
  }
}
